import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

import {
  patchSubscriptionBatteryOptions,
  postSubscriptionBatteryChoices,
} from '@/api/subscription.api';
import { useSubscriptionStore } from '@/stores/index';

import type { BatteryChoices, BatteryOptionAvailable, Provider } from '@/types/subscription.type';

import { EBatteryOptionAvailable } from '@/constants/subscription.constants';

const useBatteryStore = defineStore('battery', () => {
  const { setSubscriptionData } = useSubscriptionStore();

  const batteryChoices: Ref<BatteryChoices | null> = ref(null);

  const isLoadingBatteryChoices = ref<boolean>(false);
  const isLoadingSaveBatteryOption = ref<boolean>(false);

  const findBatteryChoices = async (subscriptionId: string, provider: Provider): Promise<void> => {
    isLoadingBatteryChoices.value = true;
    try {
      batteryChoices.value = await postSubscriptionBatteryChoices(subscriptionId, { provider });
    } catch (error) {
      batteryChoices.value = null;
      throw error;
    } finally {
      isLoadingBatteryChoices.value = false;
    }
  };

  const saveBatteryOption = async (
    subscriptionId: string,
    {
      typeBattery,
      msbSize,
    }: {
      typeBattery: BatteryOptionAvailable;
      msbSize: number | null;
    }
  ): Promise<boolean> => {
    isLoadingSaveBatteryOption.value = true;
    try {
      const subscription = await patchSubscriptionBatteryOptions(subscriptionId, {
        requested: true,
        batteryOption: {
          type: typeBattery,
          size:
            typeBattery === EBatteryOptionAvailable.MSB && !!msbSize && msbSize > 0
              ? msbSize
              : null,
        },
      });
      setSubscriptionData(subscription);
      return true;
    } catch {
      return false;
    } finally {
      isLoadingSaveBatteryOption.value = false;
    }
  };

  const saveNoBattery = async (subscriptionId: string): Promise<boolean> => {
    isLoadingSaveBatteryOption.value = true;
    try {
      const subscription = await patchSubscriptionBatteryOptions(subscriptionId, {
        requested: false,
        batteryOption: null,
      });
      setSubscriptionData(subscription);
      return true;
    } catch {
      return false;
    } finally {
      isLoadingSaveBatteryOption.value = false;
    }
  };

  return {
    batteryChoices,
    isLoadingBatteryChoices,
    isLoadingSaveBatteryOption,
    findBatteryChoices,
    saveBatteryOption,
    saveNoBattery,
  };
});

export default useBatteryStore;
